import { colorAnimTypeList } from '@src/js/js-keyframe-animation/style-converter/constants';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IShapeBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IAnimCategory } from '@src/slide/domain/ui-model/element-modeles/property/animation/constants';
import { AnimType } from '@tencent/slide-sdk-model';
import cloneDeep from 'lodash/cloneDeep';
import { IAnimCategoryManager, ElementType, IServices } from '@src/slide/application/service/anim-category-manager/interface';
import { ISelectionService } from '../selection/selection-service.interface';
import { ISelectedAnimManager } from '../selection/selected-anim-manager.interface';
import { IAnimModel } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

export class AnimCategoryManager implements IAnimCategoryManager {
    private disableAnimTypeMap: Map<ElementType, AnimType[]>;
    private selectionService: ISelectionService | undefined;
    private selectedAnimManager: ISelectedAnimManager | undefined;

    public constructor() {
        const disableList = [AnimType.FontColor, AnimType.BrushColor, AnimType.GrowWithColor];
        this.disableAnimTypeMap = new Map([
            [ElementType.GROUP, colorAnimTypeList],
            [ElementType.TABLE, colorAnimTypeList],
            [ElementType.CHART, colorAnimTypeList],
            [ElementType.CONNECTOR, disableList],
            [ElementType.IMAGE_NO_MEDIA, disableList],
            [ElementType.IMAGE_WITH_MEDIA, colorAnimTypeList],
            [ElementType.SHAPE_NO_TEXT, disableList],
            [ElementType.SHAPE_WITH_TEXT, []],
            [ElementType.SHAPE_WITH_ADDON, colorAnimTypeList],
        ]);
    }

    public setServiceDelegates(services: IServices): void {
        this.selectionService = services.selectionService;
        this.selectedAnimManager = services.selectedAnimManager;
    }

    /**
     * 根据选中元素及选中动画，获取动画数据
     * @param animCategoryList 动画范围（入场、退场、强调、路径、全部）
     * @returns
     */
    public getAnimCategoryList(animCategoryList: IAnimCategory[]): IAnimCategory[] {
        const elementModelList = this.getElementModelList();
        return this.getAnimCategoryListByElement(elementModelList, animCategoryList);
    }

    /**
     * 根据元素，获取动画数据
     * @param elementModelList 元素uiModel列表
     * @param animCategoryList 动画范围（入场、退场、强调、路径、全部）
     * @returns 打标后的最终数据IAnimCategory[]
     */
    public getAnimCategoryListByElement(
        elementModelList: IElementBaseModel[],
        animCategoryList: IAnimCategory[],
    ): IAnimCategory[] {
        // 所有需disable动画类型集合，不同类型元素需disable的动画类型不同
        let disableAnimTypeList = elementModelList?.reduce((accDisableList, elementModel) => {
            const disableList = this.getDisabledAnimTypeList(elementModel) || [];
            (accDisableList as AnimType[]).push(...disableList);
            return accDisableList;
        }, []) as AnimType[];

        // 动画类型去重
        disableAnimTypeList = [...new Set(disableAnimTypeList)];

        // 对不支持动画类型进行打标
        return this.organizeList(disableAnimTypeList, animCategoryList);
    }

    /**
     * 获取元素不支持的动画类型列表
     * @param elementModel 元素uiModel
     * @returns AnimType[]
     */
    public getDisabledAnimTypeList(elementModel: IElementBaseModel): AnimType[] {
        const elementType = this.getElementType(elementModel);
        return this.disableAnimTypeMap.get(elementType) ?? [];
    }

    /**
     * 获取元素对应的细分类型
     * @param elementModel 元素uiModel
     * @returns 元素细分类型
     */
    private getElementType(elementModel: IElementBaseModel): ElementType {
        const elementType = elementModel.type as unknown as ElementType;
        // 选中组合中的元素，禁用动画类型数量同组合元素
        if (elementModel?.parent?.type === UIModelType.GROUP) return ElementType.GROUP;
        if (elementType !== ElementType.SHAPE && elementType !== ElementType.IMAGE) return elementType;
        const handlerMap = {
            [ElementType.SHAPE]: () => this.getShapeType(elementModel as IShapeBaseModel),
            [ElementType.IMAGE]: () => this.getImageType(elementModel as IPictureModel),
        };
        return handlerMap[elementType]();
    }

    /**
     * 获取形状的细分类型
     * @param elementModel shapeModel
     * @returns 形状的细分类型
     */
    private getShapeType(elementModel: IShapeBaseModel): ElementType {
        // 插件
        if (elementModel.props?.addon) return ElementType.SHAPE_WITH_ADDON;
        // 形状，且带有文本
        if (elementModel.paragraphList?.[0]?.textRuns?.length) return ElementType.SHAPE_WITH_TEXT;
        // 形状，无文本
        return ElementType.SHAPE_NO_TEXT;
    }

    /**
     * 获取图片的细分类型
     * @param elementModel pictureModel
     * @returns 图片的细分类型
     */
    private getImageType(elementModel: IPictureModel): ElementType {
        // 音视频
        if (elementModel.video || elementModel.audio) return ElementType.IMAGE_WITH_MEDIA;
        // 纯图片
        return ElementType.IMAGE_NO_MEDIA;
    }

    /**
     * 给不支持动画类型打标
     * @param disableAnimTypeList 不支持动画类型列表
     * @param animCategoryList 全量数据
     * @returns 打标后的animCategoryList
     */
    private organizeList(disableAnimTypeList: AnimType[], animCategoryList: IAnimCategory[]): IAnimCategory[] {
        const cloneList = cloneDeep(animCategoryList);
        cloneList.forEach((animCategory) => {
            animCategory.children.forEach((animConfig) => {
                if (!disableAnimTypeList.includes(animConfig.id)) return;
                animConfig.disabled = true;
            });
        });

        return cloneList;
    }

    /**
     * 获取选中元素及选中动画去重的元素列表
     * @returns IElementBaseModel[]
     */
    private getElementModelList(): IElementBaseModel[] {
        const elementList = [...this.getSelectedElements()];
        const animTargetModelList = this.getSelectedAnimList()?.map(animModel => animModel.elementModel);
        animTargetModelList?.forEach((elementModel) => {
            if (elementList.includes(elementModel)) return;
            elementList.push(elementModel);
        });

        return elementList;
    }

    /**
     * 获取选中动画列表
     * @returns IAnimModel[]
     */
    private getSelectedAnimList(): IAnimModel[] {
        return this.selectedAnimManager?.selectedArray ?? [];
    }

    /**
     * 获取选中元素列表
     * @returns IElementBaseModel[]
     */
    private getSelectedElements(): IElementBaseModel[] {
        return this.selectionService?.selectedElements.selectedArray ?? [];
    }
}
